import { Scrollable } from "@/components/Scrollable/Scrollable";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import { getDefaultKitIcon } from "@/components/display/display-name/defaults";
import { Image } from "@/components/ui/Image";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useKitData, useKitsData } from "@/data/use-kits-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useSharedGroupUuid } from "@/hooks/use-shared-group-uuid";
import { UpdateKitBodyDto } from "@/lib/gen-api";
import { Kit } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState } from "react";
import { toast } from "sonner";

type LinkProjectTemplateAreaProps = {
  kit: Kit;
  updateKit: (body: UpdateKitBodyDto) => Promise<unknown>;
};

function PresetOption({
  preset,
  disabled,
  onSelect,
}: {
  preset: Kit;
  disabled: boolean;
  onSelect: (presetUuid: string) => void;
}) {
  return (
    <button
      type="button"
      className="flex w-[100px] cursor-pointer flex-col gap-2 disabled:cursor-not-allowed disabled:opacity-50"
      disabled={disabled}
      onClick={() => onSelect(preset.uuid)}
    >
      <div className="bg-background-3 flex h-[50px] w-[100px] items-center justify-center overflow-hidden">
        {preset.cover_image ? (
          <Image src={preset.cover_image} className="w-full" />
        ) : (
          <FontAwesomeIcon
            icon={getDefaultKitIcon({ type: "project_preset" })}
            className="text-foreground-5 text-2xl"
          />
        )}
      </div>
      <div className="truncate text-sm" title={preset.name}>
        {preset.name}
      </div>
    </button>
  );
}

export function LinkProjectTemplateArea({
  kit,
  updateKit,
}: LinkProjectTemplateAreaProps) {
  const groupUuid = useGroupUuid();
  const sharedGroupUuid = useSharedGroupUuid();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  const { kit: linkedPreset } = useKitData(
    groupUuid,
    kit.preset_kit_uuid ?? null,
  );

  const { kits: presets, isLoading } = useKitsData(groupUuid, {
    type: "project_preset",
    isPublished: true,
  });

  const groupPresets = presets.filter(
    (preset) => preset.group_uuid !== sharedGroupUuid,
  );
  const defaultPresets = presets.filter(
    (preset) => preset.group_uuid === sharedGroupUuid,
  );
  const hasPresets = groupPresets.length > 0 || defaultPresets.length > 0;

  const handleLink = async (presetUuid: string) => {
    setIsLinking(true);
    try {
      await updateKit({ preset_kit_uuid: presetUuid });
      toast.success("Workbook template linked successfully.");
      setDialogOpen(false);
    } catch {
      toast.error("Failed to link workbook template. Please try again.");
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-lg font-bold">Workbook Template</h3>
      {kit.preset_kit_uuid ? (
        <div className="flex flex-col gap-1">
          <div className="text-sm text-gray-500">
            This workbook is linked to a workbook template.
          </div>
          {linkedPreset && <KitDisplayName kit={linkedPreset} />}
        </div>
      ) : (
        <div className="flex flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-500">
            Link a workbook template to enable live skills from that template.
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={isLoading || !hasPresets}>
                <FontAwesomeIcon icon={faLink} />
                Link Workbook Template
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Link Workbook Template</DialogTitle>
              </DialogHeader>
              {!hasPresets ? (
                <div className="text-foreground-5 text-sm">
                  No workbook templates are available in this group.
                </div>
              ) : (
                <div className="flex flex-col gap-6">
                  {groupPresets.length > 0 && (
                    <div className="flex flex-col gap-4">
                      <h4 className="text-accent text-md font-semibold">
                        Group Workbook Templates
                      </h4>
                      <Scrollable
                        innerClassName="flex flex-row flex-wrap gap-4 max-h-60"
                        fromColorClassName="from-background-1"
                      >
                        {groupPresets.map((preset) => (
                          <PresetOption
                            key={preset.uuid}
                            preset={preset}
                            disabled={isLinking}
                            onSelect={handleLink}
                          />
                        ))}
                      </Scrollable>
                    </div>
                  )}
                  {defaultPresets.length > 0 && (
                    <div className="flex flex-col gap-4">
                      <h4 className="text-md font-semibold">
                        Default Workbook Templates
                      </h4>
                      <Scrollable
                        innerClassName="flex flex-row flex-wrap gap-4 max-h-60"
                        fromColorClassName="from-background-1"
                      >
                        {defaultPresets.map((preset) => (
                          <PresetOption
                            key={preset.uuid}
                            preset={preset}
                            disabled={isLinking}
                            onSelect={handleLink}
                          />
                        ))}
                      </Scrollable>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
